'use client';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MapPin,
  Plane,
  ShieldCheck,
  Star,
  Users,
  BriefcaseBusiness,
  Car,
  Route,
  CalendarCheck,
  MessageCircle,
  ChevronDown,
  Building2,
  Compass,
  BookOpenText,
  KeyRound,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const createWhatsAppUrl = (vehicle = '') => {
  const message = `Halo NAYVERA INDO TRANSPORT,

Saya ingin menanyakan ketersediaan sewa mobil Bandung.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan Sopir / Lepas Kunci
Tanggal mulai:
Jam mulai:
Tanggal selesai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi mengenai:
• Ketersediaan kendaraan
• Harga dan pilihan paket
• Fasilitas yang didapat
• Syarat dan ketentuan
• Biaya tambahan jika ada

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
};

const fleet = [
  {
    name: 'Toyota Agya / Daihatsu Ayla',
    category: 'City Car',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '5 Penumpang',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    features: ['Power Steering', 'AC Dingin', 'Audio Bluetooth', 'Eco Mode'],
    price: 'Rp 300.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Pilihan praktis untuk mobilitas harian, perjalanan singkat, dan aktivitas dalam kota Bandung.',
  },
  {
    name: 'Toyota Avanza',
    category: 'Family MPV',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/Avanza.jpeg',
    features: ['AC Double Blower', 'Audio Sistem', 'Kabin Luas', '+1 lagi'],
    price: 'Rp 550.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Pilihan kendaraan untuk kebutuhan keluarga, aktivitas bersama rekan kerja, dan perjalanan harian.',
  },
  {
    name: 'Toyota Innova Reborn',
    category: 'Family & Business',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel / Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/REBORN.jpeg',
    features: ['AC Double Blower', 'Kabin Nyaman', 'Captain Seat opt', '+1 lagi'],
    price: 'Rp 700.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Cocok untuk perjalanan keluarga, kunjungan bisnis, dan perjalanan antarkota dengan kabin yang nyaman.',
  },
  {
    name: 'Toyota Innova Zenix',
    category: 'Executive MPV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Bensin / Hybrid',
    capacity: '7 Penumpang',
    image: '/assets/images/ZENIX G.webp',
    features: ['Panoramic Roof', 'Captain Seat', 'Electric Parking', '+1 lagi'],
    price: 'Rp 850.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Alternatif kendaraan untuk perjalanan keluarga maupun kebutuhan profesional yang mengutamakan kenyamanan.',
  },
  {
    name: 'Toyota Fortuner',
    category: 'Premium SUV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel',
    capacity: '7 Penumpang',
    image: '/assets/images/Fortuner.jpeg',
    features: ['4x2 / 4x4', 'Leather Seat', 'Push Start Button', '+1 lagi'],
    price: 'Rp 1.600.000',
    priceLabel: '12 jam · dengan driver',
    description: 'SUV untuk kebutuhan perjalanan bisnis, acara khusus, dan perjalanan bersama keluarga.',
  },
  {
    name: 'Toyota Hiace Premio',
    category: 'Premium Van',
    status: 'Tersedia',
    transmission: 'Manual',
    fuel: 'Diesel',
    capacity: '14 Penumpang',
    image: '/assets/images/Hiace-Premio.jpeg',
    features: ['High Roof', 'AC per Kepala', 'Reclining Seat', '+1 lagi'],
    price: 'Rp 1.800.000',
    priceLabel: '12 jam · dengan driver',
    description: 'Pilihan kendaraan untuk wisata rombongan, perjalanan perusahaan, dan kegiatan keluarga besar.',
  },
];

const services = [
  {
    icon: KeyRound,
    title: 'Sewa Mobil Lepas Kunci',
    description:
      'Pilihan bagi pelanggan yang membutuhkan kendaraan untuk mobilitas mandiri di wilayah Bandung. Persyaratan dan ketersediaan unit dikonfirmasi saat pemesanan.',
  },
  {
    icon: Users,
    title: 'Sewa Mobil dengan Sopir',
    description:
      'Perjalanan lebih praktis ditemani driver berpengalaman yang menguasai rute jalanan Bandung, kawasan wisata, hingga jalur alternatif.',
  },
  {
    icon: Plane,
    title: 'Antar Jemput Bandara',
    description:
      'Layanan penjemputan atau pengantaran tepat waktu dari dan menuju Bandara Husein Sastranegara, Kertajati, maupun Bandara Soekarno-Hatta.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Transportasi Bisnis di Bandung',
    description:
      'Dukungan armada prima untuk agenda meeting korporat, kunjungan kerja, instansi, maupun mobilisasi proyek di kawasan Bandung dan Cimahi.',
  },
  {
    icon: Route,
    title: 'Perjalanan Luar Kota dari Bandung',
    description:
      'Layanan carter atau drop-off perjalanan dari Bandung menuju Jakarta, Bogor, Cirebon, Semarang, atau kota lainnya sesuai jadwal Anda.',
  },
  {
    icon: Compass,
    title: 'Wisata Lembang & Ciwidey',
    description:
      'Eksplorasi destinasi wisata populer di Bandung seperti dataran tinggi Lembang, Ciwidey, hingga Pangalengan bersama keluarga tercinta.',
  },
];

const useCases = [
  {
    icon: CalendarCheck,
    title: 'Mobilitas Harian di Kota Kembang',
    description:
      'Solusi fleksibel untuk keperluan harian, urusan keluarga, atau menghadiri acara spesial di berbagai sudut kota Bandung.',
  },
  {
    icon: Building2,
    title: 'Agenda Korporat & Kedinasan',
    description:
      'Mendukung mobilitas tamu VIP, perjalanan dinas kantor, serta kunjungan kerja instansi pemerintahan maupun swasta.',
  },
  {
    icon: Plane,
    title: 'Drop / Pick-up Bandara',
    description:
      'Ketepatan waktu penjemputan dari bandara menuju hotel atau rumah di Bandung menjadi prioritas kenyamanan perjalanan.',
  },
  {
    icon: Users,
    title: 'Gathering & Rombongan Keluarga',
    description:
      'Tersedia pilihan Hiace dan Elf berkapasitas besar untuk menampung keluarga besar dalam satu kendaraan yang sama.',
  },
  {
    icon: Star,
    title: 'Trip Wisata Bandung Utara & Selatan',
    description:
      'Nikmati liburan tanpa lelah menyetir menembus jalur menanjak di kawasan Dago, Lembang, maupun Ciwidey.',
  },
  {
    icon: Route,
    title: 'Antar Kota Lintas Provinsi',
    description:
      'Layanan perjalanan jarak jauh aman dan nyaman berangkat langsung dari Bandung menuju berbagai destinasi.',
  },
];

const areaGroups = [
  {
    title: 'Pusat Kota Bandung',
    description: 'Cakupan layanan cepat di jantung kota.',
    places: [
      'Bandung Wetan',
      'Coblong (Dago)',
      'Sukajadi',
      'Cicendo',
      'Lengkong',
      'Buahbatu',
    ],
  },
  {
    title: 'Bandung Raya',
    description: 'Menjangkau wilayah penyangga kota.',
    places: [
      'Kota Cimahi',
      'Kabupaten Bandung',
      'Bandung Barat',
      'Cileunyi & Rancaekek',
      'Soreang',
      'Padalarang',
    ],
  },
  {
    title: 'Kawasan Wisata',
    description: 'Destinasi liburan favorit wisatawan.',
    places: [
      'Lembang',
      'Dago Atas',
      'Ciwidey',
      'Setiabudi',
      'Ciumbuleuit',
      'Pangalengan',
    ],
  },
  {
    title: 'Bandara & Antarkota',
    description: 'Akses penghubung transportasi utama.',
    places: [
      'Bandara Husein Sastranegara',
      'Bandara Kertajati',
      'Stasiun Hall Bandung',
      'Terminal Cicaheum / Leuwipanjang',
      'Jakarta & Sekitarnya',
      'Jalur Tol Cipularang',
    ],
  },
];

const reasons = [
  {
    icon: Car,
    title: 'Armada Terawat & Bersih',
    text: 'Seluruh unit rutin diservis, dibersihkan, dan dicek kondisinya demi keamanan berkendara.',
  },
  {
    icon: MessageCircle,
    title: 'Konsultasi Mudah via WhatsApp',
    text: 'Respon cepat untuk membahas detail rute, tanggal pemesanan, dan penyesuaian kebutuhan.',
  },
  {
    icon: MapPin,
    title: 'Paham Rute Bandung & Luar Kota',
    text: 'Driver profesional yang ramah serta menguasai jalur alternatif untuk menghindari macet.',
  },
  {
    icon: ShieldCheck,
    title: 'Transparan & Terpercaya',
    text: 'Informasi harga, fasilitas, dan ketentuan disampaikan secara jelas sebelum kesepakatan.',
  },
];

const articles = [
  {
    number: '01',
    category: 'Panduan Wisata',
    title: 'Tips Memilih Sewa Mobil Bandung untuk Liburan Keluarga ke Lembang dan Ciwidey',
    paragraphs: [
      'Bandung selalu menjadi magnet utama pariwisata berkat udara sejuk dan panorama alamnya yang memukau. Berwisata ke kawasan dataran tinggi seperti Lembang di utara atau Ciwidey di selatan membutuhkan kendaraan yang prima, mengingat kontur jalanan yang didominasi tanjakan curam dan kelokan.',
      'Memilih layanan rental mobil dengan sopir di Bandung adalah keputusan bijak bagi Anda yang ingin menikmati liburan tanpa stres menghadapi kemacetan akhir pekan atau kelelahan menyetir. Driver berpengalaman kami tidak hanya mengantar, tetapi juga paham jalur-jalur alternatif tercepat menuju destinasi wisata favorit.',
      'Pastikan untuk merencanakan itinerary perjalanan Anda dengan matang dan menginformasikannya kepada admin NAYVERA agar durasi pemakaian unit dapat disesuaikan secara efisien tanpa terburu-buru.',
    ],
  },
  {
    number: '02',
    category: 'Solusi Korporat',
    title: 'Keuntungan Menggunakan Rental Mobil Bandung untuk Perjalanan Bisnis & Dinas',
    paragraphs: [
      'Bagi pelaku bisnis, profesional, maupun instansi yang sedang mengadakan kunjungan kerja di Kota Bandung dan Cimahi, ketepatan waktu dan kenyamanan mobilitas adalah kunci utama kesuksesan agenda. Mengandalkan transportasi umum tentu kurang efisien untuk mobilitas berpindah-pindah tempat meeting.',
      'NAYVERA INDO TRANSPORT menyediakan pilihan armada premium seperti Toyota Innova Reborn, Zenix, hingga Fortuner yang memberikan kesan elegan dan profesional saat mendatangi lokasi klien atau tempat acara korporat.',
      'Dengan layanan profesional berstandar tinggi, Anda dapat fokus sepenuhnya pada materi pekerjaan atau rapat penting, sementara urusan navigasi dan parkir ditangani sepenuhnya oleh pengemudi kami yang berpengalaman.',
    ],
  },
  {
    number: '03',
    category: 'Tips & Trik',
    title: 'Perbedaan Sewa Mobil Lepas Kunci dan Dengan Sopir di Bandung',
    paragraphs: [
      'Memilih antara opsi lepas kunci atau menggunakan pengemudi sangat bergantung pada kenyamanan pribadi dan kelengkapan syarat administratif. Opsi lepas kunci memberikan kebebasan penuh privasi bagi Anda yang ingin mengeksplorasi Bandung selayaknya kendaraan pribadi.',
      'Namun, layanan sewa mobil dengan sopir jauh lebih direkomendasikan bagi wisatawan luar kota yang belum terlalu akrab dengan tata letak jalanan Bandung, area satu arah (one-way), atau titik-titik rawan kemacetan di akhir pekan.',
      'Apapun pilihan Anda, pastikan untuk menanyakan ketentuan durasi sewa harian (per tanggal atau per 12/24 jam), batas wilayah operasional, serta kebijakan biaya tambahan seperti bahan bakar, tol, dan parkir.',
    ],
  },
  {
    number: '04',
    category: 'Airport Transfer',
    title: 'Panduan Praktis Antar Jemput Bandara Husein Sastranegara & Kertajati',
    paragraphs: [
      'Perjalanan udara dari atau menuju Bandung kini semakin fleksibel dengan adanya pilihan Bandara Husein Sastranegara di dalam kota maupun Bandara Internasional Jawa Barat (BIJB) Kertajati di Majalengka.',
      'Jika Anda mendarat di Kertajati dan membutuhkan perjalanan darat langsung menuju pusat Kota Bandung, memesan layanan antar jemput khusus bandara dari NAYVERA adalah solusi paling praktis dan bebas repot membawa koper banyak.',
      'Cukup informasikan nomor penerbangan, jadwal kedatangan, serta jumlah penumpang kepada tim kami, dan pengemudi kami akan siap sedia menyambut Anda di area kedatangan tepat waktu.',
    ],
  },
  {
    number: '05',
    category: 'Rekomendasi Armada',
    title: 'Pilihan Mobil Kapasitas Besar: Hiace dan Elf untuk Rombongan di Bandung',
    paragraphs: [
      'Mengadakan agenda rekreasi keluarga besar, study tour, atau rombongan kantor tentu akan lebih berkesan jika berada dalam satu kabin kendaraan yang luas ketimbang menggunakan banyak mobil kecil terpisah.',
      'Toyota Hiace Premio atau microbus yang kami sediakan menawarkan konfigurasi kabin lega, pendingin udara (AC) merata hingga baris belakang, serta kursi yang empuk untuk menjamin kenyamanan perjalanan jauh.',
      'Diskusikan jumlah peserta rombongan Anda dengan admin kami agar rekomendasi jenis kendaraan berkapasitas besar dapat disiapkan secara optimal.',
    ],
  },
  {
    number: '06',
    category: 'Panduan Pemesanan',
    title: 'Langkah Mudah Booking Rental Mobil di NAYVERA INDO TRANSPORT',
    paragraphs: [
      'Proses pemesanan kendaraan di NAYVERA dirancang sangat mudah dan cepat melalui platform WhatsApp. Anda tidak perlu repot mengisi formulir yang rumit di website.',
      'Cukup klik tombol konsultasi, lalu kirimkan format pemesanan yang mencakup tanggal sewa, jam penjemputan, alamat lengkap titik jemput di Bandung, tujuan destinasi, serta jenis armada yang diminati.',
      'Tim admin kami akan segera merespon ketersediaan unit, rincian biaya transparan, serta syarat ketentuan yang berlaku sebelum konfirmasi pemesanan Anda disetujui.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa kisaran harga sewa mobil Bandung di NAYVERA?',
    answer:
      'Harga sewa sangat bervariasi mulai dari 300 ribuan tergantung jenis kendaraan, durasi pemakaian (harian/mingguan), pilihan lepas kunci atau dengan sopir, serta rute tujuan perjalanan Anda.',
  },
  {
    question: 'Apakah tersedia layanan sewa mobil Bandung lepas kunci?',
    answer:
      'Ya, kami menyediakan opsi lepas kunci untuk unit tertentu dengan syarat dan ketentuan verifikasi data diri yang berlaku demi keamanan bersama. Silakan tanyakan ketersediaan unitnya via WhatsApp.',
  },
  {
    question: 'Bagaimana jika saya ingin menyewa mobil lengkap dengan sopir?',
    answer:
      'Layanan dengan sopir sangat populer di Bandung. Driver kami ramah, profesional, berpengalaman, dan sangat menguasai rute jalanan kota maupun jalur wisata pegunungan.',
  },
  {
    question: 'Wilayah mana saja di Bandung yang masuk dalam area jangkauan?',
    answer:
      'Kami melayani seluruh wilayah Kota Bandung, Kabupaten Bandung, Cimahi, Bandung Barat, Lembang, Ciwidey, hingga penjemputan dari stasiun dan bandara terdekat.',
  },
  {
    question: 'Apakah bisa melayani penjemputan dari Bandara Kertajati atau Soekarno-Hatta?',
    answer:
      'Tentu saja! Kami melayani rute antar-jemput dari Bandara Husein Sastranegara, Bandara Kertajati Majalengka, hingga Bandara Soekarno-Hatta Tangerang menuju Bandung.',
  },
  {
    question: 'Apakah mobil bisa digunakan untuk perjalanan ke luar kota Bandung?',
    answer:
      'Bisa. Mobil dapat disewa untuk perjalanan luar kota seperti ke Jakarta, Bogor, Cirebon, Tasikmalaya, Semarang, atau kota lainnya. Harap konfirmasikan rute tujuan saat booking.',
  },
  {
    question: 'Bagaimana cara konfirmasi pemesanan (booking)?',
    answer:
      'Anda cukup menghubungi kami via WhatsApp, mengisi format data pemesanan (tanggal, jam, armada, rute), dan admin kami akan memberikan konfirmasi ketersediaan beserta rincian harganya.',
  },
  {
    question: 'Apakah biaya BBM, tol, dan parkir sudah termasuk dalam harga?',
    answer:
      'Hal ini tergantung pada jenis paket sewa yang Anda pilih (All-in atau Unit+Sopir saja). Detail komponen biaya seperti tol, parkir, dan BBM akan diinformasikan secara transparan oleh admin.',
  },
];

export default function SewaMobilBandungPage() {
  const whatsappUrl = createWhatsAppUrl();

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#07111f]">
        <div className="absolute inset-0">
          <img
            src="/assets/images/AYLA DAN AGYA.jpg"
            alt="Sewa mobil Bandung NAYVERA INDO TRANSPORT"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07111f] via-[#07111f]/95 to-[#07111f]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[1.15fr_.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-2 text-sm font-medium text-[#e4c45a]">
              <MapPin size={16} />
              Sewa Mobil Bandung & Sekitarnya
            </div>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sewa Mobil Bandung
              <span className="mt-2 block text-[#d4af37]">
                Mulai dari 300 Ribuan Terpercaya
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              NAYVERA INDO TRANSPORT menyediakan solusi rental mobil profesional
              di Bandung untuk kebutuhan liburan keluarga, perjalanan bisnis,
              wisata Lembang & Ciwidey, antar-jemput bandara, hingga carter luar kota.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={19} />
                Konsultasi & Booking WhatsApp
                <ArrowRight size={18} />
              </a>

              <a
                href="#armada"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 font-semibold text-white backdrop-blur transition hover:border-[#d4af37]/60 hover:bg-white/10"
              >
                Lihat Pilihan Armada
              </a>
            </div>

            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ['300 Ribuan', 'Tarif terjangkau*'],
                ['Lepas Kunci', 'Opsi fleksibel'],
                ['Dengan Sopir', 'Driver berpengalaman'],
                ['Bandung Raya', 'Cakupan luas'],
              ].map(([value, label]) => (
                <div
                  key={label}
                  className="border-l border-[#d4af37]/50 pl-4"
                >
                  <div className="font-bold text-[#e4c45a]">{value}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-400">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
                  <Car size={25} />
                </div>

                <div>
                  <p className="font-bold text-white">Rencana Perjalanan di Bandung?</p>
                  <p className="text-sm text-slate-400">
                    Konsultasikan kebutuhan Anda sekarang
                  </p>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan durasi sewa',
                  'Pilih armada sesuai jumlah penumpang',
                  'Tentukan titik jemput di Bandung',
                  'Konfirmasikan harga & fasilitas',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="mt-0.5 shrink-0 text-[#d4af37]"
                    />
                    <p className="text-sm leading-6 text-slate-300">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-5 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={18} />
                Tanyakan Ketersediaan Unit
              </a>

              <p className="mt-4 text-center text-xs leading-5 text-slate-400">
                Respon cepat via WhatsApp oleh tim NAYVERA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div>
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Pusat Rental Mobil Bandung
            </p>

            <h2 className="text-3xl font-bold leading-tight text-[#07111f] sm:text-4xl">
              Solusi Transportasi Terbaik di
              <span className="text-[#b89528]"> Kota Bandung & Sekitarnya</span>
            </h2>

            <p className="mt-6 text-base leading-8 text-slate-600">
              Mencari layanan rental mobil yang aman, bersih, dan memuaskan di Bandung? 
              NAYVERA INDO TRANSPORT hadir memberikan kemudahan mobilitas untuk warga lokal 
              maupun wisatawan luar kota yang ingin mengeksplorasi keindahan Kota Kembang. 
              Mulai dari city car mungil hingga mobil keluarga berkapasitas besar tersedia lengkap.
            </p>

            <p className="mt-4 text-base leading-8 text-slate-600">
              Kami memberikan kebebasan bagi Anda untuk memilih paket sewa mobil lepas kunci 
              bagi yang ingin berkendara mandiri, atau menggunakan jasa sopir profesional yang 
              siap mengantar Anda keliling kota tanpa harus pusing memikirkan rute dan kemacetan.
            </p>
          </div>

          <div className="rounded-3xl bg-[#07111f] p-8 shadow-xl">
            <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
              <ShieldCheck size={25} />
            </div>

            <h3 className="text-2xl font-bold text-white">
              Keunggulan Layanan NAYVERA
            </h3>

            <div className="mt-7 space-y-4">
              {[
                'Pilihan armada tahun muda & terawat',
                'Opsi lepas kunci atau dengan sopir',
                'Jangkauan luas wilayah Bandung Raya',
                'Tarif transparan tanpa biaya tersembunyi',
                'Pelayanan ramah dan responsif 24 jam',
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <CheckCircle2
                    size={20}
                    className="mt-0.5 shrink-0 text-[#d4af37]"
                  />
                  <span className="text-sm leading-6 text-slate-300">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#07111f] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Layanan NAYVERA di Bandung
            </p>

            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Pilihan Jenis Layanan Rental Mobil
              <span className="text-[#d4af37]"> Sesuai Kebutuhan</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Sesuaikan model layanan kendaraan dengan agenda perjalanan Anda di Bandung.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => {
              const Icon = service.icon;

              return (
                <article
                  key={service.title}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/10 text-[#e4c45a] transition group-hover:bg-[#d4af37] group-hover:text-[#07111f]">
                    <Icon size={23} />
                  </div>

                  <h3 className="mt-6 text-xl font-bold text-white">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    {service.description}
                  </p>

                  <a
                    href={createWhatsAppUrl(service.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                  >
                    Konsultasikan layanan ini
                    <ArrowRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Agenda Perjalanan
            </p>

            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Kendaraan Andal untuk
              <span className="text-[#b89528]"> Berbagai Keperluan</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#d4af37]/50 hover:shadow-lg"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={21} />
                  </div>

                  <h3 className="mt-5 text-lg font-bold text-[#07111f]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET / ARMADA (Desain Baru Sesuai Referensi) */}
      <section id="armada" className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
                Katalog Armada
              </p>

              <h2 className="text-3xl font-bold text-[#07111f] sm:text-4xl">
                Pilihan Armada Sewa Mobil
                <span className="text-[#b89528]"> Bandung</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-600">
                Berikut adalah deretan armada terbaik kami yang siap disewa untuk menemani 
                perjalanan Anda di Bandung dan sekitarnya.
              </p>
            </div>

            <a
              href="/fleet-armada-page"
              className="inline-flex items-center gap-2 font-semibold text-[#07111f] transition hover:text-[#b89528]"
            >
              Lihat Armada Lengkap
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {fleet.map((car) => (
              <article
                key={car.name}
                className="group flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/70 hover:shadow-xl"
              >
                <div>
                  <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100">
                    <img
                      src={car.image}
                      alt={`${car.name} untuk sewa mobil Bandung NAYVERA`}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                      {car.status}
                    </div>

                    <div className="absolute right-3 top-3 rounded-full bg-[#d4af37] px-3 py-1 text-xs font-bold text-[#07111f] shadow-sm">
                      {car.category}
                    </div>

                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Users size={13} className="text-[#d4af37]" />
                      {car.capacity}
                    </div>
                  </div>

                  <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#b89528]">
                    {car.category}
                  </p>

                  <h3 className="mt-1 text-xl font-bold text-[#07111f]">
                    {car.name}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⚙️ {car.transmission}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⛽ {car.fuel}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {car.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
                      >
                        ✨ {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 border-t border-slate-100 pt-4">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Mulai dari</p>
                      <p className="text-lg font-black text-[#07111f]">{car.price}</p>
                      <p className="text-[11px] text-slate-400">{car.priceLabel}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-xl bg-[#d4af37] px-4 py-2 text-center text-xs font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
                      >
                        Booking
                      </a>
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1 rounded-xl border border-emerald-500/40 bg-emerald-50/50 px-3 py-1.5 text-center text-xs font-bold text-emerald-600 transition hover:bg-emerald-500/10"
                      >
                        <MessageCircle size={13} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-[#0b1726] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Cakupan Wilayah
            </p>

            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Area Layanan Rental Mobil
              <span className="text-[#d4af37]"> Bandung & Sekitarnya</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Kami melayani penjemputan di berbagai titik strategis di wilayah Bandung Raya 
              serta rute perjalanan ke luar kota.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {areaGroups.map((area) => (
              <article
                key={area.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
              >
                <div className="flex items-center gap-2 text-[#e4c45a]">
                  <MapPin size={18} />
                  <h3 className="font-bold text-white">{area.title}</h3>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-400">
                  {area.description}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {area.places.map((place) => (
                    <li
                      key={place}
                      className="border-b border-white/5 pb-2 text-sm text-slate-400 last:border-0"
                    >
                      {place}
                    </li>
                  ))}
                </ul>

                <a
                  href={createWhatsAppUrl(`Perjalanan area ${area.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                >
                  Tanyakan area ini
                  <ArrowRight size={15} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT */}
      <section className="overflow-hidden bg-[#07111f]">
        <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[520px]">
            <img
              src="/assets/images/Armada Hiace Nayvera.jpeg"
              alt="Armada NAYVERA untuk perjalanan bandara dan rombongan di Bandung"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#07111f]" />
          </div>

          <div className="flex items-center px-6 py-20 lg:px-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
                Airport Transfer & Drop Off
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">
                Antar Jemput Bandara
                <span className="text-[#d4af37]"> Nyaman dari Bandung</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-300">
                Butuh transportasi cepat dan tepat waktu menuju Bandara Husein Sastranegara, 
                Kertajati, atau Bandara Soekarno-Hatta? NAYVERA siap melayani perjalanan Anda 
                dengan armada bersih dan pengemudi profesional.
              </p>

              <div className="mt-8">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
                >
                  <MessageCircle size={18} />
                  Pesan Antar Jemput Bandara
                  <ArrowRight size={17} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UNIQUE SEO ARTICLES SECTION */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Wawasan & Panduan Sewa Mobil
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Artikel & Tips Seputar
              <span className="text-[#b89528]"> Rental Mobil di Bandung</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Informasi lengkap untuk membantu merencanakan perjalanan wisata, bisnis, dan keluarga Anda di Bandung.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {articles.map((art) => (
              <article
                key={art.number}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-[#d4af37]/60 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#07111f] px-3.5 py-1 text-xs font-bold text-[#e4c45a]">
                    {art.category}
                  </span>
                  <span className="text-2xl font-black text-slate-300">
                    {art.number}
                  </span>
                </div>

                <h3 className="mt-5 text-xl font-bold text-[#07111f]">
                  {art.title}
                </h3>

                <div className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                  {art.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Kenapa Memilih Kami
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Alasan Percayakan Perjalanan Anda
              <span className="text-[#b89528]"> Kepada NAYVERA</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  className="rounded-2xl border border-slate-200 p-6 transition hover:border-[#b89528]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-bold text-[#07111f]">{r.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="mt-4 text-slate-600">
              Temukan jawaban seputar layanan sewa mobil Bandung bersama NAYVERA.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition open:border-[#d4af37]"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-[#07111f]">
                  {faq.question}
                  <ChevronDown size={18} className="transition group-open:rotate-180 text-[#b89528]" />
                </summary>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER BANNER */}
      <section className="bg-[#07111f] py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
            Siap Berangkat ke Bandung?
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Pesan Kendaraan Anda Sekarang di NAYVERA
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Dapatkan pelayanan cepat, ramah, dan transparan untuk setiap kebutuhan perjalanan Anda.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-8 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
            >
              <MessageCircle size={20} />
              Hubungi WhatsApp Sekarang
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}